import { sanitizeTimeSeriesBars } from './barSeries';

export const REPLAY_V2_DATASETS = [
  'chain', 'index:SPX', 'index:ES', 'greeks:exposures', 'greeks:gexTrend',
  'greeks:feedObservation', 'greeks:dynamicLevels', 'greeks:rankedOptions', 'mi',
];
export const REPLAY_DATE_CACHE_CAPACITY = 5;

const DATASETS = new Set(REPLAY_V2_DATASETS);
const STATUSES = new Set(['complete', 'partial', 'missing', 'unavailable', 'notRequested']);
const MODES = new Set(['minute', 'minuteMember', 'event']);
const GREEK_TYPES = new Set(['delta', 'gamma', 'theta', 'vega']);
const MINUTE_MS = 60_000;

const isObject = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const isNullableNumber = (value) => value === null || isFiniteNumber(value);
const isNullableCount = (value) => value === null
  || (Number.isInteger(value) && value >= 0);
const hasString = (value) => typeof value === 'string' && value.length > 0;
const isTradingDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || '');
const hasOwn = (value, field) => Object.prototype.hasOwnProperty.call(value, field);

function validRange(range) {
  return isObject(range) && isFiniteNumber(range.from) && isFiniteNumber(range.to)
    && range.from < range.to;
}

function validCoverage(entry) {
  return isObject(entry) && STATUSES.has(entry.status) && MODES.has(entry.mode)
    && Array.isArray(entry.keyFields) && entry.keyFields.every(hasString)
    && Array.isArray(entry.availableRanges) && entry.availableRanges.every(validRange)
    && Array.isArray(entry.missingRanges) && entry.missingRanges.every(validRange)
    && (!hasOwn(entry, 'supplementalAvailableRanges')
      || (Array.isArray(entry.supplementalAvailableRanges)
        && entry.supplementalAvailableRanges.every(validRange)))
    && isNullableCount(entry.expectedKeyCount)
    && Number.isInteger(entry.observedKeyCount) && entry.observedKeyCount >= 0;
}

function validChainRow(row, symbol) {
  const numbers = [
    'strike', 'S', 'distance', 'accumulatedVolume', 'totalNetVol',
    'cumulativeDeltaNet', 'cumulativeGammaNet', 'tradeCount',
    'cumulativeBuyVol', 'cumulativeSellVol', 'cumulativeGammaBuy',
    'cumulativeGammaSell', 'cumulativeDeltaBuy', 'cumulativeDeltaSell',
  ];
  return isObject(row) && row.sym === symbol && hasString(row.optionType)
    && numbers.every((field) => isNullableNumber(row[field]));
}

function validMiRecord(record) {
  const required = [
    'id', 'tradingDate', 't', 'producerType', 'producerName', 'sourceModule',
    'postScope', 'status', 'priority', 'headline', 'body', 'summaryBullets',
    'keyLevels', 'displayPayload',
  ];
  return required.every((field) => hasOwn(record, field))
    && hasString(record.id)
    && isTradingDate(record.tradingDate)
    && isFiniteNumber(record.t)
    && hasString(record.producerType)
    && hasString(record.producerName)
    && (record.sourceModule === null || hasString(record.sourceModule))
    && hasString(record.postScope)
    && hasString(record.status)
    && Number.isInteger(record.priority)
    && typeof record.headline === 'string'
    && typeof record.body === 'string'
    && Array.isArray(record.summaryBullets)
    && record.summaryBullets.every((item) => typeof item === 'string')
    && (isObject(record.keyLevels) || Array.isArray(record.keyLevels))
    && isObject(record.displayPayload);
}


function validRecord(dataset, record) {
  if (!isObject(record)) return false;
  if (dataset === 'chain') {
    return isFiniteNumber(record.t) && isNullableNumber(record.spot)
      && isObject(record.rowsBySym)
      && Object.entries(record.rowsBySym).every(([symbol, row]) => validChainRow(row, symbol));
  }
  if (dataset.startsWith('index:')) {
    return record.symbol === dataset.slice(6) && isFiniteNumber(record.time)
      && ['open', 'high', 'low', 'close'].every((field) => isNullableNumber(record[field]));
  }
  if (dataset === 'greeks:exposures') {
    return isFiniteNumber(record.t) && GREEK_TYPES.has(record.greekType)
      && ['callsTotal', 'putsTotal', 'netExposure'].every((field) => isNullableNumber(record[field]));
  }
  if (dataset === 'greeks:gexTrend') {
    return isFiniteNumber(record.t) && hasString(record.trendType)
      && isNullableNumber(record.strengthValue);
  }
  if (dataset === 'greeks:feedObservation') return isFiniteNumber(record.t);
  if (dataset === 'greeks:dynamicLevels') {
    return isFiniteNumber(record.t) && hasString(record.levelType)
      && isNullableNumber(record.strikePrice);
  }
  if (dataset === 'greeks:rankedOptions') {
    return isFiniteNumber(record.t) && hasString(record.greekType)
      && hasString(record.optionType) && Number.isInteger(record.rank)
      && isNullableNumber(record.strikePrice);
  }
  return dataset === 'mi' && validMiRecord(record);
}

export function isValidReplayDatasetRecord(dataset, record) {
  return DATASETS.has(dataset) && validRecord(dataset, record);
}

function validSession(session) {
  return isObject(session) && session.timeZone === 'America/New_York'
    && hasString(session.calendarRevision) && isFiniteNumber(session.open)
    && isFiniteNumber(session.close) && session.open < session.close
    && session.minuteCadenceMs === MINUTE_MS
    && Number.isInteger(session.expectedMinuteCount) && session.expectedMinuteCount >= 0;
}

export function isValidReplaySessionMetadata(session) {
  return validSession(session);
}

export function isValidReplayCoverage(coverage) {
  return isObject(coverage)
    && Object.keys(coverage).length > 0
    && Object.entries(coverage).every(([dataset, entry]) => DATASETS.has(dataset) && validCoverage(entry));
}

export function validateReplayV2Payload(payload) {
  if (payload?.ok === false) return { ok: false, reason: payload.reason || 'unavailable' };
  if (payload?.schemaVersion !== 2 || !isTradingDate(payload.tradingDate)
    || (payload.requestedRange != null && !validRange(payload.requestedRange))
    || !isFiniteNumber(payload.generatedAt)
    || !isValidReplaySessionMetadata(payload.session) || !isValidReplayCoverage(payload.coverage)
    || !isObject(payload.datasets)) return { ok: false, reason: 'invalid_response' };
  for (const [dataset, records] of Object.entries(payload.datasets)) {
    if (!DATASETS.has(dataset) || !Array.isArray(records)
      || !records.every((record) => validRecord(dataset, record))) {
      return { ok: false, reason: 'invalid_response' };
    }
  }
  for (const [dataset, entry] of Object.entries(payload.coverage)) {
    const records = payload.datasets[dataset];
    const expectedRanges = [...entry.availableRanges, ...entry.missingRanges];
    const expectedRecords = entry.mode === 'event' ? records : records?.filter((record) => (
      rangeContains(expectedRanges, record.t ?? record.time)
    ));
    if (expectedRecords
      && new Set(expectedRecords.map((record) => replayDatasetCoverageKey(dataset, record))).size
      !== entry.observedKeyCount) {
      return { ok: false, reason: 'invalid_response' };
    }
  }
  return { ok: true, ...payload };
}

export function replayDatasetRecordKey(dataset, record) {
  if (dataset === 'chain') return String(record.t);
  if (dataset.startsWith('index:')) return String(record.time);
  if (dataset === 'greeks:exposures') return `${record.t}:${record.greekType}`;
  if (dataset === 'greeks:gexTrend') return `${record.t}:${record.trendType}`;
  if (dataset === 'greeks:feedObservation') return String(record.t);
  if (dataset === 'greeks:dynamicLevels') return `${record.t}:${record.levelType}`;
  if (dataset === 'greeks:rankedOptions') {
    return `${record.t}:${record.greekType}:${record.optionType}:${record.rank}`;
  }
  if (dataset === 'mi') return String(record.id);
  return null;
}
function replayDatasetCoverageKey(dataset, record) {
  if (dataset === 'greeks:gexTrend') return String(record.t);
  return replayDatasetRecordKey(dataset, record);
}


export function mergeReplayDatasets(base = {}, incoming = {}) {
  const merged = {};
  for (const dataset of new Set([...Object.keys(base), ...Object.keys(incoming)])) {
    const records = new Map();
    for (const record of [...(base[dataset] || []), ...(incoming[dataset] || [])]) {
      const key = replayDatasetRecordKey(dataset, record);
      if (key != null) records.set(key, record);
    }
    merged[dataset] = [...records.values()].sort((left, right) => {
      const delta = (left.t ?? left.time ?? 0) - (right.t ?? right.time ?? 0);
      return delta || replayDatasetRecordKey(dataset, left)
        .localeCompare(replayDatasetRecordKey(dataset, right));
    });
  }
  return merged;
}

function compactRanges(ranges) {
  return (ranges || [])
    .filter(validRange)
    .sort((left, right) => left.from - right.from || left.to - right.to)
    .reduce((merged, range) => {
      const latest = merged[merged.length - 1];
      if (!latest || latest.to < range.from) return [...merged, { ...range }];
      latest.to = Math.max(latest.to, range.to);
      return merged;
    }, []);
}

function completeGreekRanges(dataset, records) {
  const membersByMinute = new Map();
  for (const record of records || []) {
    if (!Number.isFinite(record?.t)) continue;
    const minute = Math.floor(record.t / MINUTE_MS) * MINUTE_MS;
    if (!membersByMinute.has(minute)) membersByMinute.set(minute, new Set());
    if (dataset === 'greeks:exposures') membersByMinute.get(minute).add(record.greekType);
  }
  const minutes = [...membersByMinute.entries()]
    .filter(([, members]) => dataset === 'greeks:gexTrend' || members.size === GREEK_TYPES.size)
    .map(([minute]) => minute);
  return compactRanges(minutes.map((minute) => ({ from: minute, to: minute + MINUTE_MS })));
}

function completeCachedRanges(dataset, records) {
  if (dataset === 'greeks:exposures' || dataset === 'greeks:gexTrend') {
    return completeGreekRanges(dataset, records);
  }
  if (dataset !== 'chain' && !dataset.startsWith('index:')) return [];
  const minutes = (records || []).flatMap((record) => {
    if (!validRecord(dataset, record)) return [];
    const timestamp = dataset === 'chain' ? record.t : record.time;
    return Number.isFinite(timestamp)
      ? [Math.floor(timestamp / MINUTE_MS) * MINUTE_MS]
      : [];
  });
  return compactRanges(minutes.map((minute) => ({ from: minute, to: minute + MINUTE_MS })));
}

function localOnlyCoverage(dataset) {
  const keyFields = dataset === 'chain' ? ['t']
    : dataset.startsWith('index:') ? ['time']
      : dataset === 'greeks:exposures' ? ['t', 'greekType']
        : dataset === 'greeks:gexTrend' ? ['t', 'trendType']
          : dataset === 'greeks:feedObservation' ? ['t']
            : dataset === 'greeks:dynamicLevels' ? ['t', 'levelType']
              : dataset === 'greeks:rankedOptions'
                ? ['t', 'greekType', 'optionType', 'rank'] : ['id'];
  const mode = dataset === 'greeks:exposures' ? 'minuteMember'
    : ['greeks:dynamicLevels', 'greeks:rankedOptions', 'mi'].includes(dataset)
      ? 'event' : 'minute';
  return {
    status: 'unavailable',
    mode,
    keyFields,
    cadenceMs: MINUTE_MS,
    gapToleranceMs: 0,
    expectedKeyCount: null,
    observedKeyCount: 0,
    observationDataset: null,
    queryComplete: null,
    availableRanges: [],
    missingRanges: [],
    firstRecordAt: null,
    lastRecordAt: null,
    reason: 'LOCAL_CAPTURE_ONLY',
  };
}

export function supplementReplayCoverageWithLocalDatasets(coverage = {}, localDatasets = {}) {
  const supplemented = { ...coverage };
  let changed = false;
  const datasets = new Set(Object.keys(localDatasets));
  for (const dataset of datasets) {
    const entry = supplemented[dataset] || localOnlyCoverage(dataset);
    if (!supplemented[dataset]) {
      supplemented[dataset] = entry;
      changed = true;
    }
    const localSupplementalRanges = completeCachedRanges(dataset, localDatasets[dataset]);
    if (!localSupplementalRanges.length) continue;
    const supplementalAvailableRanges = compactRanges([
      ...(entry.supplementalAvailableRanges || []),
      ...localSupplementalRanges,
    ]);
    supplemented[dataset] = { ...entry, supplementalAvailableRanges };
    changed = true;
  }
  return changed ? supplemented : coverage;
}

function normalizedTime(value) {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  const parsed = typeof value === 'string' && !Number.isFinite(numeric)
    ? Date.parse(value)
    : numeric;
  if (!Number.isFinite(parsed)) return null;
  return parsed < 10_000_000_000 ? parsed * 1000 : parsed;
}

function bucket(value) {
  if (Array.isArray(value)) return value;
  return value?.spx || value?.SPX || [];
}

function validLocalRecord(dataset, record) {
  if (!validRecord(dataset, record)) return false;
  if (dataset.startsWith('index:')) {
    return ['open', 'high', 'low', 'close'].every((field) => isFiniteNumber(record[field]));
  }
  if (dataset === 'greeks:exposures') {
    return ['callsTotal', 'putsTotal', 'netExposure']
      .every((field) => isFiniteNumber(record[field]));
  }
  if (dataset === 'greeks:gexTrend') return isFiniteNumber(record.strengthValue);
  if (dataset === 'greeks:dynamicLevels') return isFiniteNumber(record.strikePrice);
  if (dataset === 'greeks:rankedOptions') return isFiniteNumber(record.strikePrice);
  return true;
}

export function normalizeLocalReplayDatasets({ tradingDate, indexData, greeks, miPosts } = {}) {
  const sameDay = (record) => {
    const t = recordTime(record);
    if (!Number.isFinite(t)) return false;
    return new Date(t).toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) === tradingDate;
  };
  const datasets = {};
  for (const symbol of ['SPX', 'ES']) {
    const records = (indexData?.[symbol] || []).map((bar) => ({
      symbol,
      time: normalizedTime(bar.time ?? bar.timestamp),
      open: bar.open ?? null,
      high: bar.high ?? null,
      low: bar.low ?? null,
      close: bar.close ?? null,
    })).filter((record) => sameDay(record) && validLocalRecord(`index:${symbol}`, record));
    if (records.length) datasets[`index:${symbol}`] = records;
  }
  const exposures = bucket(greeks?.greekExposures).map((record) => ({
    t: normalizedTime(record.t ?? record.timestamp ?? record.formatted_date),
    greekType: record.greekType ?? record.greek_type,
    callsTotal: record.callsTotal ?? record.calls_total ?? null,
    putsTotal: record.putsTotal ?? record.puts_total ?? null,
    netExposure: record.netExposure ?? record.net_exposure ?? null,
  })).filter((record) => sameDay(record) && validLocalRecord('greeks:exposures', record));
  if (exposures.length) datasets['greeks:exposures'] = exposures;
  const trend = bucket(greeks?.gexTrendStrength).map((record) => ({
    t: normalizedTime(record.t ?? record.timestamp ?? record.formatted_date),
    trendType: record.trendType ?? record.trend_type,
    strengthValue: record.strengthValue ?? record.strength_value ?? record.value ?? null,
  })).filter((record) => sameDay(record) && validLocalRecord('greeks:gexTrend', record));
  if (trend.length) datasets['greeks:gexTrend'] = trend;
  const dynamic = bucket(greeks?.adaptiveDynamicLevels ?? greeks?.dynamicLevels).map((record) => ({
    t: normalizedTime(record.t ?? record.timestamp ?? record.formatted_date),
    levelType: record.levelType ?? record.level_type,
    strikePrice: record.strikePrice ?? record.strike_price ?? record.strike ?? null,
  })).filter((record) => sameDay(record) && validLocalRecord('greeks:dynamicLevels', record));
  if (dynamic.length) datasets['greeks:dynamicLevels'] = dynamic;
  const ranked = bucket(greeks?.adaptiveTopOptions ?? greeks?.topOptions).map((record) => ({
    t: normalizedTime(record.t ?? record.timestamp ?? record.formatted_date),
    greekType: record.greekType ?? record.greek_type,
    optionType: record.optionType ?? record.option_type,
    rank: Number(record.rank),
    strikePrice: record.strikePrice ?? record.strike_price ?? record.strike ?? null,
  })).filter((record) => sameDay(record) && validLocalRecord('greeks:rankedOptions', record));
  if (ranked.length) datasets['greeks:rankedOptions'] = ranked;
  const posts = (miPosts || []).map((post) => ({
    id: String(post.id || ''),
    tradingDate: post.tradingDate ?? post.trading_date ?? tradingDate,
    t: normalizedTime(post.t ?? post.createdAt ?? post.created_at),
    producerType: post.producerType ?? post.producer_type,
    producerName: post.producerName ?? post.producer_name,
    sourceModule: post.sourceModule ?? post.source_module ?? null,
    postScope: post.postScope ?? post.post_scope,
    status: post.status,
    priority: Number(post.priority),
    headline: post.headline,
    body: post.body,
    summaryBullets: post.summaryBullets ?? post.summary_bullets,
    keyLevels: post.keyLevels ?? post.key_levels,
    displayPayload: post.displayPayload ?? post.display_payload,
    expiresAt: normalizedTime(post.expiresAt ?? post.expires_at),
  })).filter((post) => post.tradingDate === tradingDate && sameDay(post) && validMiRecord(post));
  if (posts.length) datasets.mi = posts;
  return datasets;
}

function rangeContains(ranges, cutoffMs) {
  const minute = Math.floor(cutoffMs / MINUTE_MS) * MINUTE_MS;
  return (ranges || []).some((range) => range.from <= minute && minute < range.to);
}

export function replayDatasetCanProject(coverage, dataset, cutoffMs) {
  const entry = coverage?.[dataset];
  if (!entry) return false;
  if (rangeContains(entry.supplementalAvailableRanges, cutoffMs)) return true;
  return !['missing', 'unavailable', 'notRequested'].includes(entry.status);
}

export function replayDatasetAvailability(coverage, dataset, cutoffMs) {
  const entry = coverage?.[dataset];
  if (!entry) return 'unavailable';
  const serverAvailable = rangeContains(entry.availableRanges, cutoffMs);
  if (rangeContains(entry.supplementalAvailableRanges, cutoffMs) && !serverAvailable) {
    return 'partial';
  }
  if (['missing', 'unavailable', 'notRequested'].includes(entry.status)) return 'unavailable';
  if (entry.mode === 'event' && entry.queryComplete === true) return 'available';
  if (rangeContains(entry.missingRanges, cutoffMs)) return 'unavailable';
  if (!serverAvailable) return 'unavailable';
  // Coverage describes this cutoff. Projection retains earlier observations;
  // renderers carry values through gaps under docs/time-series-gap-contract.md.
  return 'available';
}

const recordTime = (record) => record?.t ?? record?.time ?? null;
const recordsThrough = (records, cutoffMs) => (records || [])
  .filter((record) => recordTime(record) <= cutoffMs);
const minuteRecords = (records, cutoffMs) => recordsThrough(records, cutoffMs)
  .filter((record) => Math.floor(recordTime(record) / MINUTE_MS)
    === Math.floor(cutoffMs / MINUTE_MS));

function latestBy(records, keyOf) {
  const latest = new Map();
  for (const record of records) latest.set(keyOf(record), record);
  return [...latest.values()];
}

function latestCompleteExposureMinute(records) {
  const typesByMinute = new Map();
  for (const record of records) {
    const minute = Math.floor(record.t / MINUTE_MS) * MINUTE_MS;
    if (!typesByMinute.has(minute)) typesByMinute.set(minute, new Set());
    typesByMinute.get(minute).add(record.greekType);
  }
  return [...typesByMinute.entries()].reduce(
    (latest, [minute, types]) => (types.size === GREEK_TYPES.size ? Math.max(latest, minute) : latest),
    -Infinity,
  );
}

function projectGreeks(datasets, cutoffMs) {
  const exposureCandidates = recordsThrough(datasets['greeks:exposures'], cutoffMs);
  const exposureMinute = latestCompleteExposureMinute(exposureCandidates);
  const exposures = Number.isFinite(exposureMinute)
    ? exposureCandidates.filter((record) => record.t < exposureMinute + MINUTE_MS)
    : [];
  const currentGreekExposures = Number.isFinite(exposureMinute)
    ? minuteRecords(exposures, exposureMinute) : [];
  const trend = recordsThrough(datasets['greeks:gexTrend'], cutoffMs);
  const latestTrendTime = trend.reduce((latest, record) => Math.max(latest, record.t), -Infinity);
  const currentGexTrend = Number.isFinite(latestTrendTime)
    ? minuteRecords(trend, latestTrendTime) : [];
  const dynamic = recordsThrough(datasets['greeks:dynamicLevels'], cutoffMs);
  const ranked = recordsThrough(datasets['greeks:rankedOptions'], cutoffMs);
  const mapExposure = (record) => ({
    timestamp: record.t, greek_type: record.greekType, calls_total: record.callsTotal,
    puts_total: record.putsTotal, net_exposure: record.netExposure,
  });
  const mapTrend = (record) => ({
    timestamp: record.t, trend_type: record.trendType, value: record.strengthValue,
  });
  const mapDynamic = (record) => ({
    timestamp: record.t, level_type: record.levelType, strike_price: record.strikePrice,
  });
  const mapRanked = (record) => ({
    timestamp: record.t, greek_type: record.greekType, option_type: record.optionType,
    rank: record.rank, strike_price: record.strikePrice,
  });
  return {
    greekExposures: { spx: exposures.map(mapExposure) },
    currentGreekExposures: currentGreekExposures.map(mapExposure),
    gexTrendStrength: { spx: trend.map(mapTrend) },
    currentGexTrend: currentGexTrend.map(mapTrend),
    dynamicLevels: { spx: latestBy(dynamic, (record) => record.levelType).map(mapDynamic) },
    adaptiveDynamicLevels: { spx: dynamic.map(mapDynamic) },
    topOptions: { spx: latestBy(
      ranked, (record) => `${record.greekType}:${record.optionType}:${record.rank}`,
    ).map(mapRanked) },
    adaptiveTopOptions: { spx: ranked.map(mapRanked) },
  };
}

export function projectReplayDatasets(datasets = {}, cutoffMs) {
  const chainRecords = recordsThrough(datasets.chain, cutoffMs);
  return {
    chain: chainRecords[chainRecords.length - 1] || null,
    indexData: {
      SPX: sanitizeTimeSeriesBars(recordsThrough(datasets['index:SPX'], cutoffMs)),
      ES: sanitizeTimeSeriesBars(recordsThrough(datasets['index:ES'], cutoffMs)),
    },
    greeks: projectGreeks(datasets, cutoffMs),
  };
}
